import AgencyTokenManager from '@/app/_module/services/agencyTokenManager';
import api from "@/app/_module/services/axiosConfig";
import config from "@/app/_module/services/config";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";

// Debounce silent status error toasts so they don't spam
let lastStatusErrorToastTime = 0;
const STATUS_ERROR_TOAST_COOLDOWN_MS = 10000; // 10 seconds between toasts
// Every status write bumps this; an auto-status retry only goes out while its write is still
// the newest, so a late replay can't put the agent back in an older status.
let statusWriteSeq = 0;
// Transaction-mode pooler drops last ~1–15 s (2026-10-06 probe). 1 + 3 + 8 s covers them.
const STATUS_BLIP_RETRY_MS = [1000, 3000, 8000];

interface PostAgencyResponse {
  // Updated to match your standardized backend response format
  statusCode: number;
  message: string;
  data: {
    agency: {
      id: string;
      name: string;
      description: string;
      contactEmail: string;
      isActive: boolean;
      parentAgencyId?: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string | null;
    };
    role: string;                    // Primary role field
    agencyRole: string;              // Duplicate for compatibility
    permissions: string[];           // Primary permissions field
    agencyPermissions: string[];     // Duplicate for compatibility
    agencyId: string;                // Agency ID for frontend state
    nextStep: string;                // Navigation step
  };
  timestamp: string;
  
  // Legacy support for old response format (just in case)
  success?: boolean;
  token?: string;
  agencyToken?: string;
  agency?: {
    id: string;
    name: string;
    description: string;
    contactEmail: string;
    isActive: boolean;
    parentAgencyId?: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  agencyRole?: string;
  agencyPermissions?: string[];
}

interface Agency {
  id: string;
  name: string;
  description: string;
  contactEmail: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface AgencyWithRole {
  agency: Agency;
  role: string;
  permissions: string[];
}

export const userServices = {
  // Accept optional agency snapshot to persist UI label immediately
  postAgency: async (
    agencyId: string,
    selectedAgency?: { id: string; name: string; description?: string | null; contactEmail?: string | null; isActive?: boolean; createdAt?: string; updatedAt?: string; },
    options?: { silent?: boolean }
  ) => {
    try {
      const response = await handleApiCall<PostAgencyResponse>(async () => {
        const res = await api.post<PostAgencyResponse>(
          getApiUrl(endpoint.auth.postAgencyToken),
          { agencyId }
        );
        return res;
      });
      
      // Handle backend response from your standardized API structure
      const rawData = response as unknown as any;
      
      // Extract data from the standardized response - handle double-nested structure
      const responseData = rawData.data || rawData;
      
      // Handle double-nested response structure: response.data.data
      const actualData = responseData.data || responseData;
      
      // Backend now returns structured data with agency and role at the expected locations
      const selectedAgencyData = actualData.agency;
      const agencyRole = actualData.role || actualData.agencyRole || '';
      const agencyPermissions = actualData.permissions || actualData.agencyPermissions || [];
      
      // Create the processed response data - only include the data we want, not the entire responseData
      const processedData = {
        statusCode: rawData.statusCode,
        message: rawData.message,
        timestamp: rawData.timestamp,
        agency: selectedAgencyData,
        agencyRole: agencyRole,
        agencyPermissions: agencyPermissions,
        role: agencyRole,  // For compatibility
        agencyId: actualData.agencyId,
        nextStep: actualData.nextStep
      };

      // Persist selected agency id for convenience
      try {
        sessionStorage.setItem('insuracentral_selected_agency_id', agencyId);
      } catch {}

      // CRITICAL: Update agency context BEFORE making any API calls
      // This ensures the axios interceptor uses the correct agency ID
      if (processedData.agency) {
        AgencyTokenManager.setCurrentAgency({
          id: processedData.agency.id,
          name: processedData.agency.name,
          description: processedData.agency.description ?? "",
          contactEmail: processedData.agency.contactEmail ?? "",
          isActive: processedData.agency.isActive ?? true,
          role: processedData.agencyRole || '',
          permissions: processedData.agencyPermissions || [],
          parentAgencyId: processedData.agency.parentAgencyId,
          createdAt: processedData.agency.createdAt ?? new Date().toISOString(),
          updatedAt: processedData.agency.updatedAt ?? new Date().toISOString(),
          deletedAt: null,
        });

        // Also set backend-supported fallback cookie for active agency context
        try {
          document.cookie = `active_agency=${processedData.agency.id}; path=/; max-age=3600; samesite=lax`;
        } catch {}
      }

      // Verify session is still valid after agency selection
      if (process.env.NODE_ENV === 'production') {
        try {
          const { getSupabaseSession } = await import('@/app/_module/services/auth');
          await getSupabaseSession();
        } catch (error) {

        }
      }

      // CRITICAL FIX: Call /auth/profile to get complete user data after agency selection
      try {
        const result = await handleApiCall(async () => {
          const response = await api.get(getApiUrl(endpoint.auth.getProfile));
          return response;
        });
        const profileData = result;

        // Store the complete user data for middleware access
        if (profileData) {
          try {
            const userData = {
              userType: profileData.userType || 'agent',
              agencyId: profileData.currentAgency?.agency?.id,
              accountStatus: profileData.accountStatus,
              timestamp: Date.now()
            };
            
            // Store in both session storage (for client-side) and cookie (for middleware)
            sessionStorage.setItem('insuracentral_user_context', JSON.stringify(userData));

            // Set cookie for middleware access
            document.cookie = `insuracentral_user_context=${encodeURIComponent(JSON.stringify(userData))}; path=/; max-age=300; samesite=lax`;
          } catch (storageError) {
            if (process.env.NODE_ENV === 'production') {

            }
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'production') {

        }
      }

      // Agency context was already set above before making API calls
      // No need to set it again here

      if (!options?.silent) {
        showToast("Agency set successfully", "success");
      }
      
      return processedData;
    } catch (error) {
      if (process.env.NODE_ENV === 'production') {

      }

      const transformedError = transformErrorResponse(error);
      const errorMessage = transformedError?.message || 'Failed to switch agency';
      
      if (!options?.silent) {
        showToast(errorMessage, "error");
      }
      
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformedError;
    }
  },

  getAgencies: async () => {
    try {
      // Try user-scoped listing first; backend may expose /auth/agency/list
      interface AgencyItem {
        id: string;
        name: string;
        description: string | null;
        contactEmail: string | null;
        isActive: boolean;
        createdAt: string;
        updatedAt: string;
      }

      const primary = await handleApiCall<{ data?: { items?: AgencyItem[] } } | { items?: AgencyItem[] } | AgencyItem[]>(async () => {
        const response = await api.get<{ data?: { items?: AgencyItem[] } } | { items?: AgencyItem[] } | AgencyItem[]>(getApiUrl(endpoint.auth.getAgencies));
        return response;
      });
      const payloadUnknown = primary as unknown;
      let list: AgencyItem[] = [];
      if (Array.isArray(payloadUnknown)) {
        list = payloadUnknown as AgencyItem[];
      } else if (payloadUnknown && typeof payloadUnknown === 'object') {
        const obj = payloadUnknown as Record<string, unknown>;
        const items = Array.isArray((obj.items as unknown)) ? (obj.items as AgencyItem[]) : undefined;
        const nestedData = obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : undefined;
        const nestedItems = nestedData && Array.isArray(nestedData.items as unknown) ? (nestedData.items as AgencyItem[]) : undefined;
        list = items ?? nestedItems ?? [];
      }

      const agencies: AgencyWithRole[] = list.map((item) => ({
        agency: {
          id: item.id,
          name: item.name,
          description: item.description ?? "",
          contactEmail: item.contactEmail ?? "",
          isActive: item.isActive,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          deletedAt: null,
        },
        role: "",
        permissions: [],
      }));

      return agencies;
    } catch (primaryError) {
      // If the primary path 404s (or similar), fall back to /agencies
      const transformed = transformErrorResponse(primaryError);
      const code = (transformed?.code || "").toString();
      const message = transformed?.message || "";
      const isNotFound = code === "404" || /cannot\s+get\s+\/auth\/agency\/list/i.test(message);
      if (isNotFound) {
        try {
          const fallback = await handleApiCall<{ data?: { items?: unknown[] } } | { items?: unknown[] } | unknown[]>(async () => {
            const response = await api.get<{ data?: { items?: unknown[] } } | { items?: unknown[] } | unknown[]>(getApiUrl(endpoint.agency.getAgencies));
            return response;
          });
          const payloadUnknown = fallback as unknown;
          let list: unknown[] = [];
          if (Array.isArray(payloadUnknown)) {
            list = payloadUnknown as unknown[];
          } else if (payloadUnknown && typeof payloadUnknown === 'object') {
            const obj = payloadUnknown as Record<string, unknown>;
            const items = Array.isArray((obj.items as unknown)) ? (obj.items as unknown[]) : undefined;
            const nestedData = obj.data && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : undefined;
            const nestedItems = nestedData && Array.isArray(nestedData.items as unknown) ? (nestedData.items as unknown[]) : undefined;
            list = items ?? nestedItems ?? [];
          }
          const agencies: AgencyWithRole[] = list.map((item: unknown) => ({
            agency: {
              id: (item as { id: string }).id,
              name: (item as { name: string }).name,
              description: (item as { description?: string | null }).description ?? "",
              contactEmail: (item as { contactEmail?: string | null }).contactEmail ?? "",
              isActive: (item as { isActive: boolean }).isActive,
              createdAt: (item as { createdAt: string }).createdAt,
              updatedAt: (item as { updatedAt: string }).updatedAt,
              deletedAt: null,
            },
            role: "",
            permissions: [],
          }));
          return agencies;
        } catch (fallbackError) {
          const transformedFallback = transformErrorResponse(fallbackError);
          // Skip the toast for silent (MFA-window) rejections and never show a
          // blank toast — this fires during sign-in boot, where the post-MFA
          // 403 burst is expected and self-heals.
          if (!transformedFallback?.silent && transformedFallback?.message) {
            showToast(transformedFallback.message, "error");
          }
          // eslint-disable-next-line @typescript-eslint/only-throw-error
          throw transformedFallback;
        }
      }
      if (!transformed?.silent && transformed?.message) {
        showToast(transformed.message, "error");
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformed;
    }
  },

  // Fetch the user's last-active ("primary") agency id so the selection screen
  // can pin/highlight it. /auth/agency/list returns { data: { activeAgencyId } }
  // (resolved from the active_agency cookie or the last_active_agency cache).
  // Returns null if it can't be determined — caller falls back to role/parent.
  getActiveAgencyId: async (): Promise<string | null> => {
    try {
      const payload = await handleApiCall<unknown>(async () => {
        return await api.get(getApiUrl(endpoint.auth.getAgencies));
      });
      // Handle both the enveloped ({ data: { activeAgencyId } }) and already
      // unwrapped ({ activeAgencyId }) shapes that handleApiCall may return.
      const obj = (payload && typeof payload === 'object') ? (payload as Record<string, unknown>) : undefined;
      const direct = obj?.activeAgencyId;
      const nested = obj?.data && typeof obj.data === 'object'
        ? (obj.data as Record<string, unknown>).activeAgencyId
        : undefined;
      const id = (direct ?? nested);
      return typeof id === 'string' && id.length > 0 ? id : null;
    } catch {
      return null;
    }
  },

  // Update user status (available, idle, on_call, dnd)
  updateUserStatus: async (userId: string, userStatus: 'available' | 'idle' | 'on_call' | 'dnd', options?: { silent?: boolean }) => {
    const seq = ++statusWriteSeq;
    try {
      const response = await handleApiCall(async () => {
        for (let attempt = 0; ; attempt++) {
          try {
            return await api.put(getApiUrl(`/agencies/users/${userId}/status`), { userStatus });
          } catch (err) {
            // Pooler drop (SentryExceptionFilter: 503 + 'ServiceUnavailable'). Setting a status
            // is a plain set, so replaying it is safe, but only while it's still the newest write.
            const body = err as { statusCode?: number; error?: string } | undefined;
            const blip = body?.statusCode === 503 && body?.error === 'ServiceUnavailable';
            if (!options?.silent || !blip || attempt >= STATUS_BLIP_RETRY_MS.length) throw err;
            await new Promise((resolve) => setTimeout(resolve, STATUS_BLIP_RETRY_MS[attempt]));
            if (seq !== statusWriteSeq) throw Object.assign(new Error(''), { silent: true });
          }
        }
      });

      if (!options?.silent) {
        showToast('Status updated successfully', 'success');
      }

      return response;
    } catch (error) {
      const transformed = transformErrorResponse(error);
      if (transformed?.silent) {
        // MFA-window 403 — interceptor recovers it silently; no toast at all.
      } else if (options?.silent) {
        // Auto-status changes (idle timer, call end restore) — show a single helpful message
        // Debounced to prevent spamming if multiple auto-status calls fail in a row
        // Only show status sync errors on CRM pages, not affiliate/auth pages
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        const isOnCrmPage = !path.startsWith('/affiliate') && !path.startsWith('/auth');
        if (isOnCrmPage) {
          const now = Date.now();
          if (now - lastStatusErrorToastTime > STATUS_ERROR_TOAST_COOLDOWN_MS) {
            lastStatusErrorToastTime = now;
            showToast('Status sync failed. Refresh the page or sign out and back in.', 'error');
          }
        }
      } else {
        showToast(transformed?.message || 'Failed to update status', 'error');
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformed;
    }
  },
};